import pythonCode from '!!raw-loader!./decrypt_demo.py'
import javaCode from '!!raw-loader!./SubscriptionDemo.java'
import javaPom from '!!raw-loader!./pom.xml'
import example from './example.json'

export function createGuideSections(meetingCode, subscription) {
  const id = subscription ? String(subscription.id) : '<从订阅列表复制实际订阅ID>'
  const callback = subscription?.callbackUrl || '<填写第三方提供的公网HTTPS回调地址>'
  const text = (value) => ({ type: 'text', text: value })
  const list = (...items) => ({ type: 'list', items })
  const code = (language, value) => ({ type: 'code', language, text: value })
  const table = (headers, rows) => ({ type: 'table', headers, rows })
  const sections = [
    {
      id: 'start',
      title: '快速开始',
      blocks: [
        text(
          'PC入口：会议列表 / 应用列表 → 目标对象「进入管理」→ 数据 → 报名结果订阅。需要目标管理权限及报名审核权限。'
        ),
        table(
          ['接入参数', '当前值 / 获取方式'],
          [
            ['会议 / 应用编码', meetingCode],
            ['订阅ID', id],
            ['接收方', subscription?.receiverName || '先选择或新增订阅'],
            ['回调地址', callback],
            ['共享密钥', '仅在新增保存或重置后的弹窗复制；已有密钥不再展示'],
          ]
        ),
        list(
          '第三方提供公网HTTPS 443回调地址，证书有效，不依赖重定向。',
          '点击「新增订阅」，填写接收方、回调地址及结果范围，先保持停用，保存。',
          '保存弹窗分别复制实际订阅ID和共享密钥，接收方将两者配对配置。示例ID 7与公开演示密钥不能用于正式订阅。',
          '点击「测试连通性 → 开始测试」，成功后启用；再验证真实报名。',
          '历史记录需手动「历史补推」，不会因启用订阅自动补发。'
        ),
        text(
          '遗失密钥：编辑 → 重置密钥 → 确认；订阅ID不变，旧密钥失效、订阅停用、未完成任务取消。接收方更新密钥并重新联调后启用。'
        ),
        text(
          '删除：订阅列表 → 删除 → 确认。删除后无法恢复，未完成任务取消；已发出的HTTP请求可能完成。重新接入需创建新的订阅ID及密钥。'
        ),
      ],
    },
    {
      id: 'protocol',
      title: '回调协议',
      blocks: [
        text(
          'POST到所配置回调地址；Content-Type: application/json; charset=UTF-8。只有选中的APPROVED、REJECTED、CANCELLED会推送；无需报名、待审核、进入会议不推送。外层schemaVersion固定2.0。'
        ),
        code(
          'http',
          `POST ${callback}
Content-Type: application/json; charset=UTF-8
X-WeMeet-Subscription-Id: ${id}
X-WeMeet-Timestamp: <当前Unix秒时间戳>
X-WeMeet-Nonce: <每次投递随机值>
X-WeMeet-Signature: <64位小写十六进制HMAC签名>`
        ),
        text(
          '订阅ID用于查找本地预配置密钥，不能单独作为身份凭据；校验原始正文签名与±300秒时间窗口。密钥原样按UTF-8使用，不能先Base64解码。'
        ),
        code(
          'text',
          `encKey = HMAC-SHA256(UTF8(secret), ASCII("wemeet:registration:v2:encryption"))
signKey = HMAC-SHA256(UTF8(secret), ASCII("wemeet:registration:v2:signing"))
signBytes = UTF8(timestamp + "\\n" + nonce + "\\n") || 原始正文bytes
signature = lowercaseHex(HMAC-SHA256(signKey, signBytes))
AES-256-GCM；AAD = ASCII("wemeet:registration:v2")
IV = 12字节随机值；认证标签 = 16字节
ciphertext = Base64URL(密文 || 认证标签)，IV也使用Base64URL，均无末尾等号`
        ),
        list(
          '签名前缀中的\\n表示一个0x0A换行字节。不能重新排版、换序、序列化正文后再验签。',
          '验签解密后，用共享持久化存储原子记录nonce，至少保留10分钟；多实例不能只用单机内存。',
          '按订阅/会议范围和eventId做持久化幂等；业务保存与事件成功记录应原子完成。',
          '对同一registrationId按revision整数（大整数）比较，避免乱序覆盖。旧事件及已成功处理事件也返回SUCCESS。',
          'test=true不写正式业务；test=false是真实数据，历史补推也是false。'
        ),
        code(
          'http',
          `HTTP/1.1 200 OK
Content-Type: application/json

{"code":"SUCCESS"}`
        ),
        text(
          '在5秒内可靠保存后确认；响应最大8KB。仅HTTP 200且code字符串为SUCCESS才成功。失败后间隔1分钟、5分钟、30分钟重试，含首次最多4次；再失败可在记录中人工重试。重试eventId/revision/正文不变，时间戳/nonce/签名会更新。'
        ),
      ],
    },
    {
      id: 'payload',
      title: '报文与字段',
      blocks: [
        text(
          '以下全为合成模拟数据。固定示例使用公开密钥和ID 7用于本地算法验证，不替换为实际配置后沿用旧签名；实际接入参数见「快速开始」。用户资料、实名及报名答案都在encryptedContent内，网络不发送其明文。'
        ),
        text('① 加密前的业务内容'),
        code('json', JSON.stringify(example.content, null, 2)),
        text('② 加密后的完整网络正文'),
        code('json', JSON.stringify(example.event, null, 2)),
        text('③ 解密后的业务内容（与①完全相同）'),
        code('json', JSON.stringify(example.content, null, 2)),
        table(
          ['字段', '含义'],
          [
            ['eventId', '事件唯一标识，重试不变'],
            ['data.registrationId', '报名ID字符串，模拟测试可能为null'],
            ['data.revision', '整数字符串，比较时使用大整数；直接连通测试为0'],
            ['occurredAt', 'UTC事件生成时间；历史补推时不是原始报名时间'],
            ['data.status', 'APPROVED报名成功/通过；REJECTED拒绝；CANCELLED取消'],
            ['user.name / realName', '用户资料姓名 / 实名表t_user_real_info.name，两个含义分开'],
            ['user.documentType / documentNumber', '实名表证件类型 / 数据库解密后的完整证件号，再整体加密推送'],
            ['user.realNameStatus', '实名审核状态：PENDING待审核、PASS通过、REFUSE拒绝；有资料不等于通过'],
            ['registrationForm[].fieldId', '表单字段ID字符串'],
            ['registrationForm[].title / fieldName', '来自表单定义的标题；定义缺失时回退提交时标题，fieldName保留兼容'],
            [
              'registrationForm[].fieldType / value',
              '原始字段类型及保存答案；多选答案可能为逗号拼接字符串，不保证数组',
            ],
            ['registeredAt / reviewedAt', '可选：原始报名时间（毫秒）/已保存审批或取消时间字符串'],
            ['identity / source / channelCode / rejectReason', '可选：报名身份、来源、渠道、原因；按保存值提供'],
          ]
        ),
        table(
          ['证件类型（按原始编码提供）', '说明'],
          [
            ['CERTIFICATES_TYPE_1 / 1', '中华人民共和国居民身份证'],
            ['CERTIFICATES_TYPE_2 / 2', '港澳居民来往内地通行证'],
            ['CERTIFICATES_TYPE_3 / 3', '台湾居民来往大陆通行证'],
            ['CERTIFICATES_TYPE_4 / 4', '外国人永久居住证'],
            ['CERTIFICATES_TYPE_5 / 5', '外国护照'],
            ['CERTIFICATES_TYPE_6 / 6', '军人证'],
            ['CERTIFICATES_TYPE_7 / 7', '外国人手机验证码认证'],
            ['CERTIFICATES_TYPE_8 / 8', '港澳台居民居住证'],
            ['CERTIFICATES_TYPE_99 / 99', '其他'],
          ]
        ),
        text(
          '无实名资料或未填写字段可以为空/缺省，表单数组可以为空。不能用昵称冒充实名；证件号在数据库解密失败时不会把库内密文当原值发送。重试保持生成时快照，表单改名不会改写旧事件。'
        ),
      ],
    },
    {
      id: 'examples',
      title: '开发示例',
      blocks: [
        text(
          '两种语言都支持原始请求验签解密和随机IV加密。--example仅使用内嵌公开数据，不访问真实用户。线上接口需另行实现nonce共享存储、eventId幂等、业务事务及HTTP成功响应；Demo不是完整生产接收服务。'
        ),
        text('Python 3.9+：保存为decrypt_demo.py'),
        code(
          'sh',
          `python3 -m pip install cryptography==46.0.3
python3 decrypt_demo.py --example
# 处理实际请求，提示输入共享密钥，或使用SUBSCRIPTION_SECRET环境变量
python3 decrypt_demo.py --body body.json --headers headers.json`
        ),
        { ...code('python', pythonCode), languageTab: 'python' },
        text(
          'Java 8+：保存为src/main/java/SubscriptionDemo.java；pom.xml如下。JDK需支持AES-256，旧JDK需配置相应加密策略。'
        ),
        { ...code('xml', javaPom), languageTab: 'java' },
        code(
          'sh',
          `mvn compile dependency:copy-dependencies
java -cp "target/classes:target/dependency/*" SubscriptionDemo --example
# 处理实际请求，终端输入共享密钥，或使用SUBSCRIPTION_SECRET环境变量
java -cp "target/classes:target/dependency/*" SubscriptionDemo body.json headers.json
# Windows将classpath中的冒号替换为分号`
        ),
        { ...code('java', javaCode), languageTab: 'java' },
        text(
          'body.json必须逐字节保存原始HTTP正文；headers.json是下面四个头组成的JSON对象，值须为本次实际值。真实请求超过5分钟不能直接重放。Demo会输出解密资料，仅用于联调，线上不要把完整实名写入日志。'
        ),
        code(
          'json',
          JSON.stringify(
            {
              'X-WeMeet-Subscription-Id': id,
              'X-WeMeet-Timestamp': '<当前秒时间戳>',
              'X-WeMeet-Nonce': '<本次nonce>',
              'X-WeMeet-Signature': '<本次签名>',
            },
            null,
            2
          )
        ),
        text(
          '公开固定测试向量：下面签名针对rawBody的UTF-8字节，不含行末换行。固定时间戳会过期，Demo自测会改用当前时间并重新签名。固定IV仅用于公开向量，正式加密必须随机生成。'
        ),
        code(
          'json',
          JSON.stringify(
            {
              secret: example.secret,
              timestamp: example.timestamp,
              nonce: example.nonce,
              signature: example.signature,
            },
            null,
            2
          )
        ),
        code('json', example.rawBody),
      ],
    },
    {
      id: 'acceptance',
      title: '联调验收',
      blocks: [
        list(
          '测试连通性 → 开始测试：停用时也可执行，不落投递任务，不自动重试。确认验签解密及SUCCESS返回。',
          '启用 → 模拟推送 → 记录：验证异步发送；它仍然是test=true。',
          '产生一条真实报名，验证APPROVED，再验证REJECTED和CANCELLED三种业务处理。不要只检查模拟请求。',
          '历史补推 → 开始补推：每批50条，保持页面打开；中断可继续或重新发起，已生成同结果的任务跳过。入队完成不代表接收成功，去记录看投递结果。',
          '旧报文缺少标题或实名字段：在历史补推中勾选「完善数据补推」，会针对尚无新版内容的结果生成新eventId及更大revision；不会改写旧任务。普通重试只发送原正文。'
        ),
        text(
          '历史补推读取当前保存的报名状态、实名和表单，不还原过去每次变更。同一配置版本的新版数据已入队后重复补推会跳过；失败记录使用记录中的重试。重置密钥或删除订阅后，旧配置任务不能再补发。'
        ),
      ],
    },
    {
      id: 'troubleshooting',
      title: '故障排查',
      blocks: [
        table(
          ['现象', '检查及处理'],
          [
            ['找不到订阅ID或密钥', '列表订阅ID列可复制；密钥只能在生成/重置时复制。两者须成对配置，不能使用示例7。'],
            ['没有推送记录', '检查启用状态及勾选结果；无需报名/待审核不发；存量需历史补推。'],
            ['有记录但尝试次数为0', '平台尚未执行投递任务，向平台提供订阅ID、记录ID和时间排查。'],
            [
              'HTTP 401/422或验签失败',
              '客户根据响应查订阅ID映射、当前密钥、原始正文及时间窗口；422不能直接等同于密钥错误。',
            ],
            ['HTTP 404', '检查回调地址、目标环境和接口是否部署。'],
            [
              'HTTP 503',
              '接收链路返回服务不可用；客户按eventId和时间查网关、解密后的业务处理及依赖，不直接推断ID或密钥错误。',
            ],
            ['ACK失败', '需要HTTP 200和JSON code=SUCCESS，不能只返回true、空响应、code=200或HTTP204。'],
            [
              '模拟成功但真实失败/无入库',
              '模拟test=true可能直接忽略；确认正式test=false支持三种状态，并检查真实数据字段校验、数据库或异步处理。',
            ],
            ['GCM解密失败', '核对secret不先解码、派生标签、AAD、Base64URL、12字节IV、末尾16字节认证标签。'],
            [
              '平台显示成功但查不到数据',
              '核对环境及test标记；接收方只能在可靠保存后确认成功，查是否提前确认或后续异步失败。',
            ],
          ]
        ),
        text(
          '排查时提供订阅ID、eventId、时间及时区、HTTP状态和失败阶段；不要提供密钥或证件号。客户返回503与平台历史入队接口503是两个环节，先确认错误出现在哪一处。'
        ),
      ],
    },
  ]
  const examples = sections.find((section) => section.id === 'examples')
  let group = null
  for (const block of examples.blocks) {
    if (block.type === 'text' && block.text.startsWith('Python 3.9+')) group = 'python'
    if (block.type === 'text' && block.text.startsWith('Java 8+')) group = 'java'
    if (block.type === 'text' && block.text.startsWith('body.json')) group = null
    if (group) block.languageTab = group
  }
  return sections
}

export function guideMarkdown(sections) {
  const lines = ['# 报名结果订阅对接指引', '', '协议2.0 · 字段内容版本2 · 2026-10-19', '']
  const escapeCell = (value) => String(value).replace(/\|/g, '\\|').replace(/\r?\n/g, '<br>')
  for (const section of sections) {
    lines.push(`## ${section.title}`, '')
    for (const block of section.blocks) {
      if (block.type === 'text') lines.push(block.text, '')
      if (block.type === 'list') lines.push(...block.items.map((item) => `- ${item}`), '')
      if (block.type === 'code') lines.push('```' + block.language, block.text, '```', '')
      if (block.type === 'table')
        lines.push(
          '| ' + block.headers.map(escapeCell).join(' | ') + ' |',
          '| ' + block.headers.map(() => '---').join(' | ') + ' |',
          ...block.rows.map((row) => '| ' + row.map(escapeCell).join(' | ') + ' |'),
          ''
        )
    }
  }
  return lines.join('\n')
}
